import { useState } from 'react'
import { Card, List, Tag, Button, Modal, Select, Space, message, Empty, Spin } from 'antd'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { hospitalsApi } from '@/api/hospitals'
import type { Hospital, HospitalExtraWithSenders } from '@/types/hospital'

interface AssignedSendersProps {
  hospital: Hospital
}

export const AssignedSenders = ({ hospital }: AssignedSendersProps) => {
  const queryClient = useQueryClient()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedSenderIds, setSelectedSenderIds] = useState<string[]>([])
  const [searchValue, setSearchValue] = useState('')

  // Get sender IDs from extra.senders
  const senderIds = (hospital.extra as HospitalExtraWithSenders)?.senders || []

  // Fetch sender hospital details
  const { data: sendersData, isLoading: sendersLoading } = useQuery({
    queryKey: ['hospitals-bulk', senderIds],
    queryFn: () => hospitalsApi.getBulkHospitals(senderIds),
    enabled: senderIds.length > 0,
  })

  // Sort senders alphabetically by name
  const senders = (sendersData?.data || []).sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  )

  // Search hospitals for adding senders
  const { data: searchData, isLoading: searchLoading } = useQuery({
    queryKey: ['hospitals-search', searchValue],
    queryFn: () => hospitalsApi.searchHospitals(searchValue, 20),
    enabled: searchValue.length >= 2,
  })

  const searchResults = searchData?.data || []

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (newSenderIds: string[]) => {
      const updatedExtra: HospitalExtraWithSenders = {
        ...(hospital.extra as HospitalExtraWithSenders),
        senders: newSenderIds,
      }
      return hospitalsApi.updateHospital(hospital.id, { extra: updatedExtra })
    },
    onSuccess: () => {
      message.success('Assigned senders updated successfully')
      queryClient.invalidateQueries({ queryKey: ['hospital', hospital.id] })
      setIsEditModalOpen(false)
      setSelectedSenderIds([])
      setSearchValue('')
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } }
      message.error(err.response?.data?.message || 'Failed to update assigned senders')
    },
  })

  const handleEdit = () => {
    setSelectedSenderIds([...senderIds])
    setIsEditModalOpen(true)
  }

  const handleModalClose = () => {
    setIsEditModalOpen(false)
    setSelectedSenderIds([])
    setSearchValue('')
  }

  const handleSave = () => {
    updateMutation.mutate(selectedSenderIds)
  }

  const handleRemoveSender = (senderId: string) => {
    setSelectedSenderIds((prev) => prev.filter((id) => id !== senderId))
  }

  const handleAddSender = (senderId: string) => {
    if (!selectedSenderIds.includes(senderId)) {
      setSelectedSenderIds((prev) => [...prev, senderId])
    }
  }

  // Get details of selected senders for display in modal
  const { data: selectedSendersData } = useQuery({
    queryKey: ['hospitals-bulk-selected', selectedSenderIds],
    queryFn: () => hospitalsApi.getBulkHospitals(selectedSenderIds),
    enabled: selectedSenderIds.length > 0 && isEditModalOpen,
  })

  // Sort selected senders alphabetically by name
  const selectedSenders = (selectedSendersData?.data || []).sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  )

  return (
    <>
      <Card
        title="Assigned Senders"
        extra={
          <Button size="small" icon={<EditOutlined />} onClick={handleEdit}>
            Edit
          </Button>
        }
      >
        {sendersLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : senders.length > 0 ? (
          <List
            dataSource={senders}
            pagination={{
              pageSize: 5,
              size: 'small',
              showSizeChanger: false,
              showTotal: (total) => `${total} sender${total !== 1 ? 's' : ''}`,
            }}
            renderItem={(sender) => (
              <List.Item key={sender.id}>
                <List.Item.Meta
                  title={
                    <Space>
                      <span>{sender.name}</span>
                      <Tag color="blue">CMS: {sender.cmsid}</Tag>
                    </Space>
                  }
                  description={`${sender.city || ''}, ${sender.state || ''}`}
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty
            description="No assigned senders"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '20px 0' }}
          />
        )}
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit Assigned Senders"
        open={isEditModalOpen}
        onOk={handleSave}
        onCancel={handleModalClose}
        width={700}
        confirmLoading={updateMutation.isPending}
        okText="Save Changes"
        cancelText="Cancel"
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          {/* Add Sender Section */}
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Add Sender Hospital
            </label>
            <Select
              showSearch
              placeholder="Search for hospital by name..."
              style={{ width: '100%' }}
              value={null}
              onSearch={setSearchValue}
              onChange={handleAddSender}
              loading={searchLoading}
              filterOption={false}
              notFoundContent={
                searchValue.length < 2 ? (
                  <div style={{ padding: '8px', color: '#999' }}>
                    Type at least 2 characters to search
                  </div>
                ) : searchLoading ? (
                  <Spin size="small" />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hospitals found" />
                )
              }
              options={searchResults
                .filter((h) => !selectedSenderIds.includes(h.id))
                .map((h) => ({
                  label: `${h.name} (CMS: ${h.cmsid}) - ${h.city}, ${h.state}`,
                  value: h.id,
                }))}
            />
          </div>

          {/* Selected Senders List */}
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Assigned Senders ({selectedSenderIds.length})
            </label>
            {selectedSenders.length > 0 ? (
              <List
                size="small"
                bordered
                dataSource={selectedSenders}
                renderItem={(sender) => (
                  <List.Item
                    key={sender.id}
                    actions={[
                      <Button
                        key="remove"
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveSender(sender.id)}
                      >
                        Remove
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space size="small">
                          <span>{sender.name}</span>
                          <Tag color="blue">CMS: {sender.cmsid}</Tag>
                        </Space>
                      }
                      description={`${sender.city || ''}, ${sender.state || ''}`}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description="No senders assigned"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: '20px 0', border: '1px solid #d9d9d9', borderRadius: '4px' }}
              />
            )}
          </div>
        </Space>
      </Modal>
    </>
  )
}
